/**
 * Hub access denial (SPEC-012, SCHED-760).
 *
 * The backend refuses every Hub request from an account without Hub access
 * with 403 `{ error: "hub_access_denied" }` — a distinct body, like
 * `capability_denied`, so the API clients neither refresh the token nor retry.
 * The browser's only answer is the Launch Pad's no-access state
 * (`/users/pad?denied=hub`: the message and Sign out). This module holds what
 * the three API clients, the dashboard and the Schedule Optimiser socket
 * share, so the decision is made in one place and is unit-testable.
 */

export const HUB_ACCESS_DENIED = "hub_access_denied";
export const HUB_ACCESS_DENIED_PATH = "/users/pad?denied=hub";
// Close code the Schedule Optimiser WebSocket proxy uses when the subscriber
// has no (or no longer has) Hub access for the schedule's port.
export const HUB_ACCESS_WS_CLOSE_CODE = 4003;
export const HUB_PORT_HEADER = "X-Omc-Port";

/** True for the backend's Hub access refusal, as thrown by axios. */
export function isHubAccessDenied(error) {
    return error?.response?.status === 403 && error.response.data?.error === HUB_ACCESS_DENIED;
}

/**
 * Where a denied page goes, or null when the page is already the Launch Pad,
 * which shows the message itself (and must not reload in a loop).
 */
export function hubAccessDeniedTarget(pathname) {
    if (typeof pathname === "string" && pathname.startsWith("/users/pad")) return null;
    return HUB_ACCESS_DENIED_PATH;
}

/** Replace the current page with the no-access screen; returns whether it did. */
export function redirectToHubAccessDenied() {
    if (typeof window === "undefined") return false;
    const target = hubAccessDeniedTarget(window.location.pathname);
    if (!target) return false;
    window.location.replace(target);
    return true;
}

// D11: the port the dashboard shows, sent as X-Omc-Port on every portal
// request so endpoints without a port of their own (vessel search, the
// per-plan reads) are gated for the port in view. Only the dashboard sets it;
// the Launch Pad and the admin pages never do.
let hubPort = null;

export function setHubPort(portName) {
    hubPort = typeof portName === "string" && portName.trim() ? portName : null;
}

export function getHubPort() {
    return hubPort;
}

/** Axios request interceptor: adds the header when a port is in view. */
export function applyHubPortHeader(config) {
    if (!hubPort || !config) return config;
    config.headers = config.headers || {};
    if (config.headers[HUB_PORT_HEADER] == null) config.headers[HUB_PORT_HEADER] = hubPort;
    return config;
}
